/**
 * ResultActions
 *
 * Standardized action group rendered ABOVE a hero's results card —
 * the post-completion equivalent of `<ContinueCard>`'s action row.
 *
 * Today every hero homepage spreads its result-page actions inline in
 * the page header (`<PageHeader actions={...}>`). The action *set* is
 * predictable across heroes: Retake, Share, Export, Open in App.
 * Visual treatment drifted (Care had no icon, CheckUp used `Play`,
 * QultureUp/TelePortal `RefreshCw`).
 *
 * `<ResultActions>` is a slot-based pattern: you compose action chips
 * with the canonical icons + tones the design system locks. Hosts
 * still own the modal opening + mutation; the component owns the
 * shape.
 *
 * Usage:
 *
 *   <ResultActions>
 *     <ResultActions.Retake onClick={() => setRetakeOpen(true)} />
 *     <ResultActions.Share onClick={...} />
 *     <ResultActions.OpenInApp ... />
 *   </ResultActions>
 *
 * The composability is intentional: passport has Export + Share but
 * no Retake; QultureUp has Retake + Open-in-App. Each hero composes
 * what it has — but every hero uses the SAME chip styling.
 */

import type { ReactNode } from 'react';
import { Download, RefreshCw, Share2, Smartphone } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button, cn } from '@tonnie/ui';

interface ResultActionsProps {
  className?: string;
  children: ReactNode;
}

export function ResultActions({ className, children }: ResultActionsProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 justify-end', className)}
      role="toolbar"
      aria-label={t('copilot.aria.resultActions')}
    >
      {children}
    </div>
  );
}

interface ChipProps {
  onClick?: () => void;
  href?: string;
  busy?: boolean;
  dataTestid?: string;
  children?: ReactNode;
}

ResultActions.Retake = function RetakeChip({ onClick, busy, dataTestid }: ChipProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={busy}
      data-testid={dataTestid}
      aria-label={t('copilot.lifecycle.retake.confirm')}
    >
      <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
      {t('common.retake')}
    </Button>
  );
};

ResultActions.Share = function ShareChip({ onClick, busy, dataTestid }: ChipProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={busy}
      data-testid={dataTestid}
      aria-label={t('common.share')}
    >
      <Share2 className="w-4 h-4 mr-2" aria-hidden="true" />
      {t('common.share')}
    </Button>
  );
};

ResultActions.Export = function ExportChip({ onClick, busy, dataTestid }: ChipProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={busy}
      data-testid={dataTestid}
      aria-label={t('common.export')}
    >
      <Download className="w-4 h-4 mr-2" aria-hidden="true" />
      {t('common.export')}
    </Button>
  );
};

ResultActions.OpenInApp = function OpenInAppChip({
  onClick,
  busy,
  dataTestid,
  children,
}: ChipProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="default"
      onClick={onClick}
      disabled={busy}
      data-testid={dataTestid}
      aria-label={t('common.openInApp')}
    >
      <Smartphone className="w-4 h-4 mr-2" aria-hidden="true" />
      {children ?? t('common.openInApp')}
    </Button>
  );
};
